import React, { useEffect, useRef, useState } from 'react';
import { theme } from '../../../theme';
import { Animated, Easing, StyleSheet, View } from 'react-native';

interface Props {
  active: boolean;
  children: React.ReactNode;
  useBorder?: boolean;
  borderColor?: string;
  borderRadius?: number;
}

const DemoAttentionPulse: React.FC<Props> = ({
  active,
  children,
  useBorder = false,
  borderColor = theme.colors.success,
  borderRadius = 8,
}) => {
  const anim = useRef(new Animated.Value(0)).current;
  const [hasBeenPressed, setHasBeenPressed] = useState(false);
  const shouldAnimate = active && !hasBeenPressed;
  const useNativePulse = !useBorder;

  useEffect(() => {
    if (!active) {
      anim.stopAnimation();
      anim.setValue(0);
      return;
    }

    if (hasBeenPressed) {
      anim.stopAnimation();
      anim.setValue(0);
      return;
    }

    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(anim, {
          toValue: 3,
          duration: 600,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: useNativePulse,
        }),
        Animated.timing(anim, {
          toValue: 0,
          duration: 600,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: useNativePulse,
        }),
      ]),
    );

    pulse.start();
    return () => pulse.stop();
  }, [active, anim, hasBeenPressed, useNativePulse]);

  if (useBorder) {
    const borderWidth = anim.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 2],
    });

    return (
      <View
        style={styles.borderWrap}
        onTouchStart={() => {
          if (shouldAnimate) setHasBeenPressed(true);
        }}>
        {shouldAnimate && (
          <Animated.View
            pointerEvents="none"
            style={[
              styles.borderOverlay,
              {
                borderColor,
                borderRadius,
                borderWidth,
              },
            ]}
          />
        )}
        {children}
      </View>
    );
  }

  const scale = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.015],
  });

  return (
    <Animated.View
      onTouchStart={() => {
        if (shouldAnimate) setHasBeenPressed(true);
      }}
      style={{
        transform: [{ scale }],
        zIndex: 10,
        elevation: 10,
        alignSelf: 'stretch',
      }}>
      {children}
    </Animated.View>
  );
};

export default React.memo(DemoAttentionPulse);

const styles = StyleSheet.create({
  borderWrap: {
    position: 'relative',
  },
  borderOverlay: {
    position: 'absolute',
    top: -1,
    left: -1,
    right: -1,
    bottom: -1,
    // zIndex: 10,
    // elevation: 10,
  },
});
